import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Col, Row } from "react-bootstrap";
import { TableHeader, WithRoleAccess } from "@services/ui-components";
import VioletBadge from "@services/ui-components/src/VioletBadge/VioletBadge";
import WarehousesTable from "../Warehouse/WarehousesTable/WarehousesTable";
import { useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { CompanyAccess, PermissionScope } from "@me-team/host/main/graphql/types";
import { StartWarehouseTutorialButton, StartWarehouseTutorialModal } from "../index";
import WarehouseJoyrideWrapper from "../Joyride/WarehouseTutorial/WarehouseJoyrideWrapper";
import {
    getCurrentStepFromLocalStorage,
    getWarehouseTutorialType,
    isWarehouseTutorialActive,
    moveStepInLocalStorage,
    StepDirection,
} from "../Joyride/WarehouseTutorial/joyrideStepHelpers";
import { useCloseTooltipsOnModalOpen } from "../../hooks/useCloseTooltipsOnModalOpen";
import { useTutorialAccess } from "../Joyride/useTutorialAccess";

const Warehouse: React.FC = () => {
    const { t } = useTranslation();
    const { branchId } = useParams();
    const [showPositionModal, setShowPositionModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [toggleProductSaleModal, setToggleProductSaleModal] = useState(false);
    const [showStartWarehouseTutorialModal, setShowStartWarehouseTutorialModal] = useState<boolean>(false);
    const tutorialActive = isWarehouseTutorialActive();
    const tutorialType = getWarehouseTutorialType();
    const startStep = getCurrentStepFromLocalStorage();
    const { tooltipResetKey, resetTooltips } = useCloseTooltipsOnModalOpen();
    const { canStartWarehouseTutorial } = useTutorialAccess();

    const handleCloseStartWarehouseTutorialModal = () => {
        resetTooltips();
        setShowStartWarehouseTutorialModal(false);
    };

    const handleAddWarehouseModal = () => setShowPositionModal((prevState) => !prevState);
    const handleToggleProductSaleModal = () => setToggleProductSaleModal((prevState) => !prevState);

    return (
        <>
            <VioletBadge
                text={t(
                    "This page displays all the branch's warehouses, as well as the total number of items in each warehouse and the number of items in stock"
                )}
            />
            <TableHeader
                title={t("Warehouses")}
                paths={[
                    { path: `/warehouses/branch/${branchId}`, label: t("Warehouse accounting") },

                    { path: `/warehouses/branch/${branchId}`, label: t("Warehouses") },
                ]}
                colSize={3}
                btnColSize={9}
                buttons={
                    <div className="mt-3 mt-lg-0">
                        <Row
                            className="g-3 justify-content-lg-end align-items-stretch flex-xxl-nowrap"
                            xs={1}
                            xxl="auto"
                        >
                            {canStartWarehouseTutorial && (
                                <>
                                    <Col xs={12} className="d-flex d-sm-none">
                                        <StartWarehouseTutorialButton
                                            classBtn="w-100 px-1"
                                            handleClick={() => setShowStartWarehouseTutorialModal(true)}
                                        />
                                    </Col>
                                    <Col xs="auto" className="d-none d-sm-flex justify-content-end">
                                        <StartWarehouseTutorialButton
                                            classBtn="w-auto px-1"
                                            iconOnly
                                            handleClick={() => setShowStartWarehouseTutorialModal(true)}
                                            tooltipResetKey={tooltipResetKey}
                                        />
                                    </Col>
                                </>
                            )}
                            <WithRoleAccess
                                permission={CompanyAccess.WarehouseOperationSaleCreate}
                                scope={PermissionScope.Self}
                            >
                                <Col xs={12} sm="auto">
                                    <div className="d-grid d-sm-block">
                                        <Button
                                            variant="outline-orange"
                                            onClick={() => {
                                                setToggleProductSaleModal(true);
                                                tutorialActive && moveStepInLocalStorage(StepDirection.NEXT);
                                                tutorialActive &&
                                                    window.dispatchEvent(new Event("warehouseStepChanged"));
                                            }}
                                            className="text-nowrap"
                                            style={{ minWidth: 45 }}
                                            data-tour="sale-products-btn"
                                        >
                                            <i className="bi bi-cart4" />
                                            <span className="ms-2">{t("Sale of products")}</span>
                                        </Button>
                                    </div>
                                </Col>
                            </WithRoleAccess>
                            <WithRoleAccess permission={CompanyAccess.WarehouseOperationListView}>
                                <Col xs={12} sm="auto">
                                    <Link
                                        to={`/warehouses/operations/${branchId}`}
                                        onClick={() => tutorialActive && moveStepInLocalStorage(StepDirection.NEXT)}
                                        className="btn btn-outline-primary fs-7 text-nowrap custom-btn align-items-center d-grid d-sm-inline-block"
                                        data-tour="warehouse-operations-btn"
                                    >
                                        {t("Warehouse operations")}
                                    </Link>
                                </Col>
                            </WithRoleAccess>
                            <WithRoleAccess permission={CompanyAccess.WarehouseCreate}>
                                <Col xs={12} sm="auto">
                                    <div className="d-grid d-sm-block">
                                        <Button
                                            variant="primary"
                                            className="fs-7 fw-bold text-nowrap"
                                            onClick={handleAddWarehouseModal}
                                        >
                                            <i className="bi bi-plus-lg" />
                                            <span className="ms-1 ">{t("Add Warehouse")}</span>
                                        </Button>
                                    </div>
                                </Col>
                            </WithRoleAccess>
                        </Row>
                    </div>
                }
                showFilters={false}
            ></TableHeader>
            <div data-tour="warehouse-selection">
                <WarehousesTable
                    show={showPositionModal}
                    handleModal={handleAddWarehouseModal}
                    branchId={Number(branchId)}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    isOpenProductSaleModal={toggleProductSaleModal}
                    onToggleProductSaleModal={handleToggleProductSaleModal}
                />
            </div>

            {showStartWarehouseTutorialModal && (
                <StartWarehouseTutorialModal
                    show={showStartWarehouseTutorialModal}
                    handleClose={handleCloseStartWarehouseTutorialModal}
                />
            )}
            {tutorialActive && tutorialType && (
                <WarehouseJoyrideWrapper tutorialType={tutorialType} startStep={startStep} />
            )}
        </>
    );
};

export default Warehouse;
